import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API, formatApiError, getStoredToken, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Phone, Microphone, StopCircle, PlayCircle, WaveSine, WarningCircle } from "@phosphor-icons/react";
import { ArrowRight } from "@phosphor-icons/react";

const COLS = ["Current Node", "VAD Event", "Transcript", "Intent", "Score", "Branch", "Time (s)"];
const LOW_BALANCE_MINOR = 200; // $2.00 — Live mic blocked at or below this for AI/Collect flows
const AI_FLOW_KINDS = new Set(["ai_llm", "collect_info"]);

const NODE_KIND_LABELS = {
  start: "Start",
  play_audio: "Play Audio",
  collect_info: "Collect Customer Info",
  ai_llm: "AI Agent",
  wait: "Wait for Customer",
  silence: "Silence",
  intent: "Customer Response",
  dtmf: "DTMF",
  transfer: "Transfer",
  hangup: "Hang Up",
  end: "End",
  caller: "Caller",
};

const flowHasAiNodes = (flow) => {
  const nodes = flow?.nodes || [];
  return nodes.some((n) => AI_FLOW_KINDS.has(n?.data?.kind));
};

/** Friendly Current Node label (AI Agent instead of ai_llm). */
const nodeLabel = (row) => {
  if (!row) return "—";
  if (row.role === "caller" || row.node === "caller") return "Caller";
  const kind = row.kind || row.node || "";
  if (kind === "ai_llm" || row.node === "ai_llm") return "AI Agent";
  return NODE_KIND_LABELS[kind] || NODE_KIND_LABELS[row.node] || row.node || kind || "—";
};

const joinTranscript = (prev, chunk) => {
  const a = (prev || "").trim();
  const b = (chunk || "").trim();
  if (!b) return a;
  if (!a) return b;
  // Cumulative updates (new text starts with old) — keep the longer one.
  if (b.startsWith(a) || b.includes(a)) return b;
  if (a.endsWith(b)) return a;
  return `${a} ${b}`.replace(/\s+/g, " ").trim();
};

export default function TestCallModal({ campaign, open, onClose }) {
  const [session, setSession] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("idle");
  const [transfer, setTransfer] = useState(null);
  const [live, setLive] = useState(false);
  const [listening, setListening] = useState(false);
  const [duplex, setDuplex] = useState(false);
  const [balanceBlocked, setBalanceBlocked] = useState(null); // error string or null
  const wsRef = useRef(null);
  const mediaRef = useRef(null);
  const audioElRef = useRef(null);
  const pendingSttRef = useRef(null);
  const lastTsRef = useRef(null);
  const pcmCtxRef = useRef(null);
  const pcmNextRef = useRef(0);
  const pcmSourcesRef = useRef([]);

  const needsAiBalance = useMemo(() => flowHasAiNodes(campaign?.flow), [campaign?.flow]);

  useEffect(() => {
    if (!open || !campaign) return undefined;
    let cancelled = false;
    setBalanceBlocked(null);
    setSession(null);
    (async () => {
      if (needsAiBalance) {
        try {
          const { data } = await api.get("/billing/balance");
          const bal = data?.cash_balance_minor ?? 0;
          if (bal <= LOW_BALANCE_MINOR) {
            if (!cancelled) {
              setBalanceBlocked(
                "Minimum balance above $2.00 is required to run a Live mic Test Call that uses AI Agent or Collect Customer Info.",
              );
              setStatus("error");
            }
            return;
          }
        } catch {
          /* backend start will still enforce */
        }
      }
      if (!cancelled) await startSession();
    })();
    return () => { cancelled = true; cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaign, needsAiBalance]);

  const cleanup = () => {
    try { wsRef.current?.close(); } catch { /* noop */ }
    try { mediaRef.current?.stop?.(); } catch { /* noop */ }
    clearPcm();
    wsRef.current = null; mediaRef.current = null;
  };

  const clearPcm = () => {
    pcmSourcesRef.current.forEach((s) => { try { s.stop(0); } catch { /* noop */ } });
    pcmSourcesRef.current = [];
    pcmNextRef.current = 0;
    try { pcmCtxRef.current?.close(); } catch { /* noop */ }
    pcmCtxRef.current = null;
  };

  const ensurePcmCtx = async () => {
    if (!pcmCtxRef.current || pcmCtxRef.current.state === "closed") {
      pcmCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      pcmNextRef.current = 0;
    }
    if (pcmCtxRef.current.state === "suspended") {
      try { await pcmCtxRef.current.resume(); } catch { /* noop */ }
    }
    return pcmCtxRef.current;
  };

  const playPcmWav = async (wavB64) => {
    try {
      const ctx = await ensurePcmCtx();
      const bin = atob(wavB64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      const decoded = await ctx.decodeAudioData(bytes.buffer.slice(0));
      const src = ctx.createBufferSource();
      src.buffer = decoded;
      src.connect(ctx.destination);
      const now = ctx.currentTime;
      const startAt = Math.max(now + 0.01, pcmNextRef.current || now);
      src.start(startAt);
      pcmNextRef.current = startAt + decoded.duration;
      pcmSourcesRef.current.push(src);
      src.onended = () => {
        pcmSourcesRef.current = pcmSourcesRef.current.filter((x) => x !== src);
      };
    } catch (err) {
      // Fallback: play via <audio> element if Web Audio decode fails
      const el = audioElRef.current;
      if (el) {
        el.src = `data:audio/wav;base64,${wavB64}`;
        el.play().catch(() => {});
      }
    }
  };

  const startSession = async () => {
    setRows([]); setTransfer(null); setStatus("starting"); setDuplex(false); setBalanceBlocked(null);
    try {
      const { data } = await api.post("/test-call/start", campaign.did_id
        ? { did_id: campaign.did_id }
        : { campaign_id: campaign.id });
      setSession(data); setStatus("ready");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setStatus("error");
      if (e.response?.status === 402 || /balance|\$2/i.test(msg || "")) {
        setBalanceBlocked(msg);
      }
      toast.error(msg);
    }
  };

  // Seconds each step took: for a wait→speech row this is the human's
  // response+speech time; for a play row it's the bot's prompt time.
  const pushDebug = (m) => setRows((r) => {
    const now = performance.now();
    const dt = lastTsRef.current != null ? (now - lastTsRef.current) / 1000 : null;
    lastTsRef.current = now;
    const row = {
      node: m.current_node, kind: m.kind, vad: m.vad_event,
      intent: m.intent, score: m.score, branch: m.branch,
      transcript: m.transcript || null,
      time: dt, human_s: null,
    };
    // The stt (transcript) message can arrive just before this wait row is
    // created — attach the buffered transcript so it isn't lost.
    if (m.kind === "wait" && !row.transcript && pendingSttRef.current) {
      row.transcript = pendingSttRef.current.transcript;
      row.human_s = pendingSttRef.current.speech_duration ?? null;
      pendingSttRef.current = null;
    }
    return [...r, row];
  });

  const applyStt = (m) => {
    const chunk = (m.transcript || "").trim();
    pendingSttRef.current = { transcript: chunk, speech_duration: m.speech_duration };
    if (!chunk) return;
    const role = m.role === "caller" ? "caller" : "bot";
    setRows((r) => {
      const c = [...r];
      // Attach to the latest wait / AI Agent row that has no transcript yet.
      for (let i = c.length - 1; i >= 0; i -= 1) {
        if ((c[i].kind === "wait" || c[i].kind === "ai_llm") && !c[i].transcript) {
          c[i] = {
            ...c[i],
            role: c[i].role || role,
            transcript: chunk,
            human_s: m.speech_duration ?? c[i].human_s,
          };
          pendingSttRef.current = null;
          return c;
        }
      }
      // Live duplex: keep one transcript line per speaker turn (append chunks).
      const last = c[c.length - 1];
      const lastRole = last?.role
        || (last?.node === "caller" ? "caller" : (last?.kind === "ai_llm" ? "bot" : null));
      if (last && last.vad === "live" && last.kind === "ai_llm" && lastRole === role) {
        c[c.length - 1] = {
          ...last,
          role,
          transcript: joinTranscript(last.transcript, chunk),
          human_s: m.speech_duration ?? last.human_s,
        };
        pendingSttRef.current = null;
        return c;
      }
      c.push({
        node: role === "bot" ? "ai_llm" : "caller",
        kind: "ai_llm",
        role,
        vad: "live",
        transcript: chunk,
        intent: null, score: null, branch: null,
        time: null, human_s: m.speech_duration ?? null,
      });
      pendingSttRef.current = null;
      return c;
    });
  };

  const downsample = (input, inRate, outRate = 16000) => {
    if (inRate === outRate) return input;
    const ratio = inRate / outRate;
    const outLen = Math.floor(input.length / ratio);
    const out = new Float32Array(outLen);
    for (let i = 0; i < outLen; i += 1) out[i] = input[Math.floor(i * ratio)] || 0;
    return out;
  };

  const startLive = async () => {
    if (!session || balanceBlocked) return;
    if (needsAiBalance) {
      try {
        const { data } = await api.get("/billing/balance");
        if ((data?.cash_balance_minor ?? 0) <= LOW_BALANCE_MINOR) {
          const msg = "Minimum balance above $2.00 is required to run a Live mic Test Call that uses AI Agent or Collect Customer Info.";
          setBalanceBlocked(msg);
          toast.error(msg);
          return;
        }
      } catch { /* backend enforces */ }
    }
    setRows([]); setTransfer(null); setStatus("live"); setLive(true); setListening(false); setDuplex(false);
    pendingSttRef.current = null; lastTsRef.current = performance.now();
    clearPcm();
    // Unlock playback inside the click, before any await, so later prompts
    // are allowed to play.
    try {
      const el = audioElRef.current;
      if (el) {
        el.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
        await el.play();
      }
      await ensurePcmCtx();
    } catch { /* autoplay may still work for the first real prompt */ }
    // Prefer sessionStorage (impersonation / backdoor-login tabs) over localStorage.
    const token = getStoredToken();
    if (!token) {
      setStatus("error");
      toast.error("Session expired — refresh or sign in again.");
      return;
    }
    const wsUrl = `${API.replace(/^http/, "ws")}/test-call/ws/${session.session_id}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      const m = JSON.parse(ev.data);
      if (m.type === "listening") { setListening(true); return; }
      if (m.type === "live_start") {
        setDuplex(true); setListening(false);
        return;
      }
      if (m.type === "live_end") {
        setDuplex(false);
        return;
      }
      if (m.type === "pcm_clear") {
        clearPcm();
        return;
      }
      if (m.type === "pcm_wav" && m.wav_b64) {
        setListening(false);
        playPcmWav(m.wav_b64);
        return;
      }
      setListening(false);
      if (m.type === "debug") pushDebug(m);
      else if (m.type === "stt") applyStt(m);
      else if (m.type === "play") playNode(m);
      else if (m.type === "done") {
        setTransfer(m.test_transfer);
        setStatus("done");
        setLive(false);
        setDuplex(false);
        stopMic();
        const secs = Number(m.ai_agent_duration_sec || 0);
        const charge = Number(m.ai_charge_minor || 0);
        if (m.ai_billing_status === "charged" && charge > 0) {
          toast.success(`AI billed ${money(charge)} for ${secs}s (see Billing → Transaction History)`);
        } else if (secs > 0 && m.ai_billing_status && m.ai_billing_status !== "charged") {
          toast.message(`AI time ${secs}s · billing ${m.ai_billing_status}`);
        }
      }
      else if (m.type === "error") {
        const msg = formatApiError(m.detail);
        toast.error(msg);
        setStatus("error");
        setLive(false);
        setDuplex(false);
        if (/balance|\$2/i.test(msg || "")) setBalanceBlocked(msg);
      }
    };
    ws.onerror = () => { toast.error("Test-call connection failed"); setLive(false); setDuplex(false); };
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      try { await ctx.resume(); } catch { /* noop */ }
      const source = ctx.createMediaStreamSource(stream);
      const proc = ctx.createScriptProcessor(4096, 1, 1);
      proc.onaudioprocess = (e) => {
        if (ws.readyState !== 1) return;
        const frame = downsample(e.inputBuffer.getChannelData(0), ctx.sampleRate, 16000);
        ws.send(frame.buffer);
      };
      // Route through a muted gain so the processor keeps firing without echoing
      // the mic back to the speakers.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      source.connect(proc); proc.connect(sink); sink.connect(ctx.destination);
      let stopped = false;
      mediaRef.current = { stop: () => {
        if (stopped) return;
        stopped = true;
        try { proc.disconnect(); source.disconnect(); sink.disconnect(); } catch { /* noop */ }
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
        try { if (ctx.state !== "closed") ctx.close(); } catch { /* noop */ }
      } };
    } catch (err) {
      toast.error("Microphone permission denied — use Simulate instead");
      setLive(false);
    }
  };

  const stopMic = () => { try { mediaRef.current?.stop?.(); } catch { /* noop */ } mediaRef.current = null; };

  const playNode = (m) => {
    let acked = false;
    const done = () => {
      if (acked) return;
      acked = true;
      try { wsRef.current?.send(JSON.stringify({ type: "played", node_id: m.node_id })); } catch { /* noop */ }
    };
    const el = audioElRef.current;
    const src = m.mode === "wav" && m.wav_b64
      ? `data:audio/wav;base64,${m.wav_b64}`
      : (m.mode === "audio" && m.audio_file_id
        ? `${API}/audio/${m.audio_file_id}?auth=${encodeURIComponent(getStoredToken() || "")}`
        : null);
    if (src && el) {
      el.onended = done;
      el.onerror = done;
      el.onloadedmetadata = () => {
        const secs = Number.isFinite(el.duration) ? el.duration : (m.duration_s || 8);
        window.setTimeout(done, Math.min(secs * 1000 + 1500, 90000));
      };
      el.src = src;
      el.play().catch(done);
      return;
    }
    if (m.prompt_text && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(m.prompt_text);
        utter.onend = done;
        utter.onerror = done;
        window.speechSynthesis.speak(utter);
        return;
      } catch { /* fall through */ }
    }
    done();
  };

  const endLive = () => {
    try { wsRef.current?.send(JSON.stringify({ type: "end" })); } catch { /* noop */ }
    stopMic(); setLive(false); setListening(false); setDuplex(false); clearPcm(); ws_close();
  };
  const ws_close = () => { try { wsRef.current?.close(); } catch { /* noop */ } };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { cleanup(); onClose(); } }}>
      <DialogContent className="max-w-3xl" data-testid="test-call-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Phone className="w-5 h-5" weight="fill" /> Test Call — {campaign?.name}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground" data-testid="test-call-blurb">
              <p>
                To test the call, click the <span className="font-semibold text-foreground">“Live Mic Call”</span> button
                {" "}to initiate a test call directly from your browser.
              </p>
              <p>
                <span className="font-semibold text-foreground">Note:</span> This is a browser-based test call only.{" "}
                <span className="font-semibold text-foreground">No CDRs or real SIP/DID resources are used.</span>
              </p>
              {needsAiBalance && (
                <p className="rounded-md border border-amber-300/80 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-2.5 py-2 text-amber-900 dark:text-amber-200" data-testid="test-call-ai-billing-note">
                  <span className="font-semibold">Important:</span> The AI Agent is active during this test call.
                  {" "}Therefore, the test call will be billed to your wallet at the applicable{" "}
                  <span className="font-semibold">AI model per-minute rate</span>.
                </p>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {balanceBlocked && (
          <div
            className="mb-3 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/40 dark:border-red-800 px-3 py-2.5 text-sm text-red-700 dark:text-red-300"
            data-testid="test-balance-error"
            role="alert"
          >
            <WarningCircle className="w-5 h-5 shrink-0 mt-0.5" weight="fill" />
            <span>{balanceBlocked}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {!live ? (
            <Button
              onClick={startLive}
              disabled={!session || !!balanceBlocked}
              className="rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              data-testid="test-live-btn"
              title={balanceBlocked || undefined}
            >
              <Microphone className="w-4 h-4 mr-1.5" weight="fill" /> Live mic call
            </Button>
          ) : (
            <Button variant="destructive" onClick={endLive} className="rounded-lg" data-testid="test-end-btn">
              <StopCircle className="w-4 h-4 mr-1.5" weight="fill" /> End call
            </Button>
          )}
          {live && (
            duplex ? (
              <span className="flex items-center gap-1.5 text-sm font-medium text-violet-600 animate-pulse" data-testid="test-duplex-indicator">
                <WaveSine className="w-4 h-4" weight="bold" /> Live conversation — speak naturally
              </span>
            ) : listening ? (
              <span className="flex items-center gap-1.5 text-sm font-medium text-primary animate-pulse" data-testid="test-listening-indicator">
                <Microphone className="w-4 h-4" weight="fill" /> Listening — please speak
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="test-speaking-indicator">
                <PlayCircle className="w-4 h-4" weight="fill" /> Bot speaking…
              </span>
            )
          )}
          <span className="text-xs text-muted-foreground ml-auto" data-testid="test-status">Status: {status}</span>
        </div>

        {transfer && (
          <div className={`rounded-lg px-3 py-2 text-sm font-medium ${transfer === "SUCCESS" ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"}`} data-testid="test-transfer-result">
            TEST TRANSFER ← {transfer}
          </div>
        )}

        <div className="rounded-xl border border-border overflow-hidden max-h-[46vh] overflow-y-auto" data-testid="test-debug-log">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 sticky top-0">
              <tr>{COLS.map((c) => <th key={c} className="text-left px-3 py-2 font-medium">{c}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Start a Live mic call.</td></tr>}
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-border" data-testid={`test-row-${i}`}>
                  <td className="px-3 py-1.5 font-medium whitespace-nowrap">{nodeLabel(r)}</td>
                  <td className="px-3 py-1.5">{r.vad || "—"}</td>
                  <td className="px-3 py-1.5 max-w-[320px] whitespace-nowrap overflow-hidden text-ellipsis" title={r.transcript || undefined}>{r.transcript || "—"}</td>
                  <td className="px-3 py-1.5">{r.intent || "—"}</td>
                  <td className="px-3 py-1.5 tabular-nums">{r.score != null ? r.score : "—"}</td>
                  <td className="px-3 py-1.5">{r.branch ? <span className="inline-flex items-center gap-1 text-primary"><ArrowRight className="w-3 h-3" />{r.branch}</span> : "—"}</td>
                  <td className="px-3 py-1.5 tabular-nums text-muted-foreground whitespace-nowrap" data-testid={`test-row-${i}-time`}>
                    {r.time != null ? `${r.time.toFixed(1)}s` : "—"}
                    {r.human_s != null ? <span className="text-primary"> · spoke {r.human_s.toFixed(1)}s</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <audio ref={audioElRef} className="hidden" />
      </DialogContent>
    </Dialog>
  );
}
